import { Modal, Button, Form } from "react-bootstrap";
import { useState, useEffect } from "react";
import axios from "axios";

const RefundModal = ({ show, onHide, saleItem }) => {
  const [reason, setReason] = useState("");
  // Use product_id as key for returnedQty for consistency with backend expectation
  const [returnedQty, setReturnedQty] = useState({});
  const [refundAmount, setRefundAmount] = useState(0);
  const [paymentType, setPaymentType] = useState("Cash");
  // const [fullRefund, setFullRefund] = useState(false); // Not used, can be removed

  const baseURL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (saleItem) {
      // Reset state when modal opens for a new saleItem
      setReason("");
      setReturnedQty({});
      setRefundAmount(0);
      setPaymentType("Cash"); // Default payment type
      // setFullRefund(false); // Reset this too if it were used

      // Initialize returnedQty for each item in the sale
      // Use product_id as the key for returnedQty
      const initialQty = {};
      saleItem.items?.forEach(item => {
        // Ensure we don't allow refunding more than available (original qty - refunded_qty)
        const maxRefundableQty = item.quantity - (item.refund_qty || 0);
        initialQty[item.product_id] = maxRefundableQty > 0 ? maxRefundableQty : 0;
      });
      setReturnedQty(initialQty);
    }
  }, [saleItem]);

  useEffect(() => {
    // Recalculate refund amount whenever returnedQty or saleItem changes
    if (saleItem && saleItem.items) {
      let total = 0;
      saleItem.items.forEach((item) => {
        // Use product_id for lookup in returnedQty
        const qtyToRefund = returnedQty[item.product_id] || 0;
        // Calculate amount for this item based on its original price and quantity to refund
        // Consider item-level discount if applicable from the original sale item
        const itemSubtotal = qtyToRefund * item.price;
        const itemDiscountAmount = itemSubtotal * ((item.discount_percentage || 0) / 100);
        const itemTaxableAmount = itemSubtotal - itemDiscountAmount;
        const itemTaxAmount = itemTaxableAmount * ((item.tax_percentage || 0) / 100);
        total += itemTaxableAmount + itemTaxAmount; // Sum up the net amount for each refunded item
      });
      setRefundAmount(total);
    }
  }, [returnedQty, saleItem]);

  const handleQtyChange = (productId, value) => {
    const numericValue = Number(value);
    // Find the original item to get its max quantity
    const originalItem = saleItem.items?.find(item => item.product_id === productId);

    if (originalItem) {
      const maxRefundableQty = originalItem.quantity - (originalItem.refund_qty || 0);
      const clampedValue = Math.max(0, Math.min(numericValue, maxRefundableQty));
      setReturnedQty((prev) => ({ ...prev, [productId]: clampedValue }));
    } else {
      setReturnedQty((prev) => ({ ...prev, [productId]: numericValue }));
    }
  };

  const handleProcessRefund = async () => {
    if (!reason) {
      alert("Please provide a refund reason.");
      return;
    }

    const storedUser = JSON.parse(localStorage.getItem("login_user"));
    const userId = storedUser?.id || null;

    // Filter out products with 0 quantity or 0 amount for refund
    const productsToRefund = saleItem.items
      .map((item) => {
        const qtyToRefund = returnedQty[item.product_id] || 0;
        if (qtyToRefund <= 0) return null; // Skip items with 0 quantity to refund

        const itemSubtotal = qtyToRefund * item.price;
        const itemDiscountAmount = itemSubtotal * ((item.discount_percentage || 0) / 100);
        const itemTaxableAmount = itemSubtotal - itemDiscountAmount;
        const itemTaxAmount = itemTaxableAmount * ((item.tax_percentage || 0) / 100);
        const itemTotalForRefund = itemTaxableAmount + itemTaxAmount;

        return {
          id: item.id, // This is the SaleItem ID
          product_id: item.product_id,
          refund_qty: qtyToRefund, // This is the quantity being refunded for this item
          price: item.price, // Original selling price of the item
          tax: item.tax_percentage || 0, // Original tax percentage
          total: itemTotalForRefund, // Total amount for this specific item refund
          amount: itemTotalForRefund, // Backend expects 'amount' for each item
        };
      })
      .filter(Boolean); // Remove null entries

    if (productsToRefund.length === 0) {
      alert("Please specify at least one item quantity to refund.");
      return;
    }

    const refundData = {
      sale_id: saleItem.id,
      user_id: userId,
      total_amount: refundAmount, // This is the sum of 'total' from productsToRefund
      reason: reason,
      payment_type: paymentType,
      comments: saleItem.comments || "", // Assuming comments can be passed from saleItem
      location: saleItem.locationid || "", // Assuming locationid is available on saleItem
      products: productsToRefund,
    };

    try {
      const response = await axios.post(`${baseURL}/process-refund-till`, refundData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = response.data;
      console.log("Refund Response:", result);
      if (result.status == "success") {
        alert(result.message || "Refund processed successfully.");
        onHide(); // Close modal and trigger re-fetch in SalesList
      } else {
        // alert("Error processing refund: " + (result.message || "Unknown error"));
        alert(result.message || "Refund processed successfully.");
        onHide();
      }
    } catch (error) {
      console.error("Refund API Error:", error.response?.data || error.message);
      alert("Error processing refund: " + (error.response?.data?.message || error.message));
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Refund Transaction</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Reason:</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
        </Form.Group>

        {saleItem?.items && saleItem.items.length > 0 && (
          <div className="mb-3">
            <h6>Items to Refund:</h6>
            {saleItem.items.map((item, index) => (
              <div key={item.id} className="d-flex justify-content-between align-items-center mb-2">
                <Form.Control
                  type="number"
                  min={0}
                  // Max quantity is original quantity minus already refunded quantity
                  max={item.quantity - (item.refund_qty || 0)}
                  value={returnedQty[item.product_id] || 0}
                  onChange={(e) => handleQtyChange(item.product_id, e.target.value)}
                  style={{ width: "20%" }}
                />
                <div style={{ width: "70%", textAlign: "right" }}>
                  {item.quantity} x {item.product_name} (Price: £{item.price.toFixed(2)})
                  {item.refund_qty > 0 && (
                    <span className="text-muted ms-2">(Refunded: {item.refund_qty})</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Refund Amount:</Form.Label>
          <Form.Control type="number" value={refundAmount.toFixed(2)} readOnly />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Refund Payment Type:</Form.Label>
          <Form.Select
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}
          >
            <option value="Cash">Cash</option>
            <option value="Credit Card">Credit Card</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Cheque">Cheque</option>
            <option value="Other">Other</option>
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" onClick={handleProcessRefund}>Process</Button>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RefundModal;
